import React, { useState, useEffect } from 'react';
import { BookOpen, GraduationCap, Users, Target, Award, Brain, Play } from 'lucide-react';
import { primaryEnglishData } from '../data/primaryEnglishData';
import aiDrillService from '../services/aiDrillService';
import AiDrillProgressModal from './AiDrillProgressModal';
import DrillQuestions from './DrillQuestions';

const PrimaryEnglish = () => {
  const [activeGrade, setActiveGrade] = useState('p1');
  const [drillState, setDrillState] = useState({
    isGenerating: false,
    drillQuestions: null,
    currentQuestion: null,
    currentTopic: null,
    currentQuestionIndex: null,
    currentTopicIndex: null,
    progress: {
      percentage: 0,
      currentStep: 0,
      elapsedTime: 0
    },
    metadata: null
  });

  const grades = [
    { id: 'p1', label: '小學一年級', icon: BookOpen },
    { id: 'p2', label: '小學二年級', icon: BookOpen },
    { id: 'p3', label: '小學三年級', icon: BookOpen },
    { id: 'p4', label: '小學四年級', icon: BookOpen },
    { id: 'p5', label: '小學五年級', icon: BookOpen },
    { id: 'p6', label: '小學六年級', icon: BookOpen }
  ];

  const currentGradeData = primaryEnglishData[activeGrade];

  // Progress simulation for AI Drill
  useEffect(() => {
    let interval;
    if (drillState.isGenerating) {
      const startTime = Date.now();
      interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(Math.floor(elapsed / 200), 100); // Simulate progress over 20 seconds
        const currentStep = Math.min(Math.floor(progress / 20), 4); // 5 steps total
        
        setDrillState(prev => ({
          ...prev,
          progress: {
            percentage: progress,
            currentStep: currentStep,
            elapsedTime: elapsed
          }
        }));
      }, 100);
    }
    return () => clearInterval(interval);
  }, [drillState.isGenerating]);

  const handleAiDrill = async (question, topic, questionIndex, topicIndex) => {
    setDrillState(prev => ({
      ...prev,
      isGenerating: true,
      currentQuestion: question,
      currentTopic: topic,
      currentQuestionIndex: questionIndex,
      currentTopicIndex: topicIndex,
      progress: {
        percentage: 0,
        currentStep: 0,
        elapsedTime: 0
      }
    }));

    try {
      const result = await aiDrillService.generateDrillQuestions(
        question,
        topic,
        currentGradeData.gradeName
      );

      setDrillState(prev => ({
        ...prev,
        isGenerating: false,
        drillQuestions: result.drillQuestions,
        metadata: result.metadata
      }));
    } catch (error) {
      console.error('AI Drill generation failed:', error);
      setDrillState(prev => ({
        ...prev,
        isGenerating: false
      }));
    }
  };

  const handleDrillComplete = () => {
    setDrillState(prev => ({
      ...prev,
      drillQuestions: null,
      currentQuestion: null,
      currentTopic: null,
      currentQuestionIndex: null,
      currentTopicIndex: null,
      metadata: null
    }));
  };

  const handleNewDrill = () => {
    if (drillState.currentQuestion && drillState.currentTopic) {
      handleAiDrill(
        drillState.currentQuestion, 
        drillState.currentTopic, 
        drillState.currentQuestionIndex, 
        drillState.currentTopicIndex
      );
    }
  };

  return (
    <div className="space-y-6">
      {/* AI Drill Progress Modal */}
      <AiDrillProgressModal
        isOpen={drillState.isGenerating}
        progress={drillState.progress}
        metadata={drillState.metadata}
      />

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-lg">
        <div className="flex items-center space-x-3 mb-4">
          <GraduationCap className="w-8 h-8" />
          <h1 className="text-3xl font-bold">Primary English Learning</h1>
        </div>
        <p className="text-blue-100 text-lg">
          Comprehensive English learning materials for Primary 1-6 students
        </p>
      </div>

      {/* Grade Navigation */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-4 border-b">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Select Grade Level</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {grades.map(grade => {
              const Icon = grade.icon;
              return (
                <button
                  key={grade.id}
                  onClick={() => setActiveGrade(grade.id)}
                  className={`flex flex-col items-center p-4 rounded-lg transition-all duration-200 ${
                    activeGrade === grade.id
                      ? 'bg-blue-100 border-2 border-blue-500 text-blue-700'
                      : 'bg-gray-50 border-2 border-transparent text-gray-600 hover:bg-gray-100 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-6 h-6 mb-2" />
                  <span className="text-sm font-medium text-center">{grade.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Grade Content */}
      {currentGradeData && (
        <div className="space-y-6">

          {/* Grade Overview */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Users className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-800">{currentGradeData.gradeName}</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center">
                  <Target className="w-5 h-5 mr-2 text-green-600" />
                  Learning Objectives
                </h3>
                <ul className="space-y-2 text-gray-600">
                  {currentGradeData.objectives.map((objective, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-green-500 mr-2">•</span>
                      {objective}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center">
                  <Award className="w-5 h-5 mr-2 text-purple-600" />
                  Key Skills
                </h3>
                <ul className="space-y-2 text-gray-600">
                  {currentGradeData.keySkills.map((skill, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-purple-500 mr-2">•</span>
                      {skill}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Topics */}
          <div className="space-y-4">
            {currentGradeData.topics.map((topic, topicIndex) => (
              <div key={topicIndex} className="bg-white rounded-lg shadow-sm border">
                <div className="p-6 border-b bg-gray-50">
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    Topic {topicIndex + 1}: {topic.title}
                  </h3>
                  <p className="text-gray-600">{topic.description}</p>
                </div>
                
                <div className="p-6">
                  {/* Purpose */}
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-700 mb-3 flex items-center">
                      <Target className="w-5 h-5 mr-2 text-blue-600" />
                      目的 (Purpose)
                    </h4>
                    <p className="text-gray-600 bg-blue-50 p-4 rounded-lg">
                      {topic.purpose}
                    </p>
                  </div>

                  {/* Sample Questions */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
                      <BookOpen className="w-5 h-5 mr-2 text-green-600" />
                      Sample Questions & Answers
                    </h4>
                                         <div className="space-y-4">
                       {topic.questions.map((question, qIndex) => (
                         <div key={qIndex} className="space-y-4">
                           {/* Original Question */}
                           <div className="border rounded-lg p-4 bg-gray-50">
                             <div className="mb-3">
                               <span className="inline-block bg-blue-100 text-blue-800 text-sm font-medium px-2 py-1 rounded mr-2">
                                 Q{qIndex + 1}
                               </span>
                               <span className="text-gray-700 font-medium">{question.question}</span>
                             </div>
                             
                             <div className="mb-3">
                               <span className="inline-block bg-green-100 text-green-800 text-sm font-medium px-2 py-1 rounded mr-2">
                                 Answer
                               </span>
                               <span className="text-gray-700">{question.answer}</span>
                             </div>
                             
                             <div className="mb-3">
                               <span className="inline-block bg-purple-100 text-purple-800 text-sm font-medium px-2 py-1 rounded mr-2">
                                 Explanation
                               </span>
                               <p className="text-gray-600 text-sm mt-1">{question.explanation}</p>
                             </div>
                             
                             {/* AI Drill Button */}
                             <div className="mt-4 pt-3 border-t border-gray-200">
                               <button
                                 onClick={() => handleAiDrill(question, topic, qIndex, topicIndex)}
                                 disabled={drillState.isGenerating}
                                 className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                                   drillState.isGenerating
                                     ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                     : 'bg-purple-600 text-white hover:bg-purple-700'
                                 }`}
                               >
                                 <Brain className="w-4 h-4" />
                                 <span>AI Drill</span>
                                 {drillState.isGenerating && drillState.currentQuestion === question && (
                                   <Play className="w-4 h-4 animate-pulse" />
                                 )}
                               </button>
                             </div>
                           </div>

                           {/* AI Drill Questions - Show only for this specific question */}
                           {drillState.drillQuestions && 
                            drillState.currentQuestionIndex === qIndex && 
                            drillState.currentTopicIndex === topicIndex && (
                             <div className="bg-white rounded-lg border p-4 shadow-sm">
                               <DrillQuestions
                                 drillQuestions={drillState.drillQuestions}
                                 onComplete={handleDrillComplete}
                                 onNewDrill={handleNewDrill}
                               />
                             </div>
                           )}
                         </div>
                       ))}
                     </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PrimaryEnglish; 